import {_} from 'lodash';
import * as ko from "knockout";
import {IParams as IParams_AbstractDialog, Step} from "./AbstractDialogOperation";
import {viewModel as Recreate_viewModel} from "./recreate";
import {ICompanySyncstep, IHistory} from "../../../index";
import PromiseRequest from "../../../req/PromiseRequest";
import {createErrorMessage, getObjectDefinition, IObjectDefinition} from "../../../util/misc";
import {getHash} from "../../../constants/RouteDefinitions";
import {ObjectTypeId, SPINNER} from "../../../constants";
import recreate_historyHtml from './recreate.history.html?raw';

export const template: string = recreate_historyHtml;

export type IParams = {
    data: () => IHistory[];
    getNumObjectsLabel: (num?: number) => string
} & IParams_AbstractDialog

type TSortFn<T> = (item: T) => string | number
type TErrorObject = { message: string, item: IHistoryProcessing }

interface IHistory2Process {
    cs: ICompanySyncstep[]
    obj: IHistory
    objDef: IObjectDefinition
}

interface IHistoryProcessing {
    cs: ICompanySyncstep
    obj: IHistory
    objDef: IObjectDefinition
}

export class viewModel extends Recreate_viewModel {

    private submitSuccess_history: IHistoryProcessing[] = []
    private submitError_history: TErrorObject[] = []
    private companySyncsteps: ICompanySyncstep[] = [];

    private restoreSubsequentSyncSteps: KnockoutObservable<boolean> = ko.observable(false)
    private companySyncsteps_sorted: ICompanySyncstep[] = [];

    constructor(private paramsRecreateHistory: IParams) {
        super(paramsRecreateHistory);
    }

    protected override getNumObjectsLabel(num: number = this.numTotalComputed()): string {
        return this.paramsRecreateHistory.getNumObjectsLabel(num)
    }

    /** Parent recreate.updateStep(Start) never populates companySyncsteps_sorted — use this flow instead. */
    protected override updateStep() {
        switch (this.step()) {
            case Step.Start:
                this.start()
                break
            default:
                super.updateStep()
                break
        }
    }

    protected start() {
        this.req_getcompanysyncsteps
            .request()
            .then((data: ICompanySyncstep[]) => {
                this.companySyncsteps = _.sortBy(data, (o: ICompanySyncstep) => o.order)
                this.syncsteps = data.map((o: ICompanySyncstep) => o.syncstep)
                this.companySyncsteps_sorted = _(this.companySyncsteps)
                    .filter((o: ICompanySyncstep) => o.syncstep.isexportable)
                    .sortBy((o: ICompanySyncstep) => o.order)
                    .value()
            })
            .then(() => {
                this.step(Step.Init)
                this.kosub.subscribe(this.historiesProcessing, () => {
                    const numTotal = this.numTotalComputed();
                    const title = this.paramsRecreateHistory.getTitle(numTotal);
                    (this.paramsRecreateHistory.dialog.title as KnockoutObservable<string>)(title);
                })
            })
            .catch((reason: string) => {
                this.step(null)
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    protected override process() {
        this.submitSuccess_history = []
        this.submitError_history = []

        const items: IHistoryProcessing[] = _.clone(this.historiesProcessing())

        return Promise.resolve()
            .then(() => {
                const doNext = (): Promise<void> => {
                    this.percentComplete((this.submitSuccess_history.length + this.submitError_history.length) / this.numTotalComputed());
                    this.messageData({message: this.getProcessItemSuccessMessage()})
                    if (!items.length) {
                        return Promise.resolve()
                    }
                    const item = items.pop()!;
                    const p = new PromiseRequest(this.serviceName);
                    this.promiseRequests.push(p);
                    return p
                        .request(this.getRequestParams_history(item))
                        .then(() => {
                            this.submitSuccess_history.push(item)
                            return doNext()
                        })
                        .catch((reason: string) => {
                            this.submitError_history.push({message: reason, item: item})
                            return doNext()
                        })
                }

                const all: Promise<any>[] = []
                for (let i = 0; i < Math.min(this.numTotalComputed(), this.maxConcurrentRequests); i++) {
                    all.push(doNext());
                }
                return Promise.all(all)

            })
            .then(() => {
                this.submitSuccess_history = _.sortBy(this.submitSuccess_history, this.STANDARD_SORT)
                this.submitError_history = _.sortBy(this.submitError_history, this.STANDARD_SORT_ERROR_OBJECT)
            })
            .then(() => this.onProcessComplete())
            .catch((reason: string) => {
                this.step(Step.Complete);
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    protected override getProcessItemSuccessMessage(): string {
        return `((restoring|td)) [${this.submitSuccess_history.length + this.submitError_history.length}/${this.numTotalComputed()}] ${SPINNER}`;
    }

    protected override successMessage(): string {
        const n = this.submitSuccess_history.length
        return `subject is restored|n:${n}|s:${this.getNumObjectsLabel(n)}`
    }

    protected override errorMessage(): string {
        const n = this.submitError_history.length
        return `subject is not restored due to error|n:${n}|s:${this.getNumObjectsLabel(n)}`
    }

    protected override getRequestParams(id: number): null {
        return null
    }

    protected getRequestParams_history(item: IHistoryProcessing) {
        return {
            objecttypeid: item.obj.objecttypeid,
            objectid: item.obj.userid,
            syncstepid: item.cs.syncstep.id,
            test: false,
        }
    }

    protected getLink_history(item: IHistory): string {
        return getHash(getObjectDefinition(item.objecttypeid)!.routeDetails!, {id: item.userid}) ?? ''
    }

    private readonly STANDARD_SORT: TSortFn<IHistoryProcessing>[] = [
        (o: IHistoryProcessing) => o.obj.objecttypeid,
        (o: IHistoryProcessing) => o.obj.displayname?.toLowerCase() ?? '',
        (o: IHistoryProcessing) => o.obj.userid ?? 0,
    ]

    private readonly STANDARD_SORT_ERROR_OBJECT: TSortFn<TErrorObject>[] = this.STANDARD_SORT.map(fn => (err: TErrorObject) => fn(err.item));

    private _histories2Process = ko.pureComputed((): IHistory2Process[] => {
        const exportable = this.companySyncsteps_sorted ?? []
        const allSteps = this.companySyncsteps ?? []
        const ret = _(this.paramsRecreateHistory.data())
            .map((o: IHistory) => {
                let companySyncsteps: ICompanySyncstep[] = []
                let startIndex = exportable.findIndex(cs => o.syncstepid == cs.syncstep.id)
                // Failed history may reference a step missing from isexportable filter — still restore that step.
                if (startIndex < 0) {
                    const startIndexAll = allSteps.findIndex(cs => o.syncstepid == cs.syncstep.id)
                    if (startIndexAll >= 0) {
                        companySyncsteps = (this.restoreSubsequentSyncSteps() && o.objecttypeid == ObjectTypeId.User)
                            ? allSteps.slice(startIndexAll)
                            : allSteps.slice(startIndexAll, startIndexAll + 1)
                    }
                } else if (this.restoreSubsequentSyncSteps() && o.objecttypeid == ObjectTypeId.User) {
                    companySyncsteps = exportable.slice(startIndex)
                } else {
                    companySyncsteps = exportable.slice(startIndex, startIndex + 1)
                }
                return {
                    obj: o,
                    objDef: getObjectDefinition(o.objecttypeid)!,
                    cs: companySyncsteps,
                }
            })
            .value()

        if (!this.restoreSubsequentSyncSteps()) {
            return ret
        }

        // filter on unique users (with most company syncsteps)
        const users = _(ret)
            .filter((o: IHistory2Process) => o.obj.objecttypeid == ObjectTypeId.User)
            .sortBy((o: IHistory2Process) => o.cs.length)
            .reverse()
            .uniqBy((o: IHistory2Process) => o.obj.userid)
            .value()
        const nonusers = ret.filter((o: IHistory2Process) => o.obj.objecttypeid != ObjectTypeId.User)
        return [...users, ...nonusers]
    })

    private histories2Process = ko.pureComputed((): IHistory2Process[] => {
        return _(this._histories2Process())
            .sortBy((o: IHistory2Process) => o.obj.displayname ?? o.obj.userid)
            .value()
    })

    private historiesProcessing = ko.pureComputed((): IHistoryProcessing[] => {
        return this.histories2Process()
            .reduce((arr: IHistoryProcessing[], o: IHistory2Process) => {
                const ret: IHistoryProcessing[] = o.cs.map((cs: ICompanySyncstep) => ({
                    obj: o.obj,
                    objDef: getObjectDefinition(o.obj.objecttypeid)!,
                    cs: cs,
                }))
                arr.push(...ret)
                return arr
            }, [])
    })

    private numTotalComputed = ko.pureComputed((): number => {
        return this.historiesProcessing().length
    })

}